import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CreditCard, TrendingUp, Calendar, DollarSign, AlertTriangle, Lock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from "recharts";

export default function MembershipsOverview() {
  const { currentBranch, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [revenueByPackage, setRevenueByPackage] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Permission gate ──────────────────────────────────────────
  const canViewOverview = hasPermission("view_memberships_overview");

  useEffect(() => {
    if (!canViewOverview) { setLoading(false); return; }

    const fetchData = async () => {
      try {
        const bq = currentBranch ? `?branchId=${currentBranch.id}` : "";

        const [dashRes, membershipsRes, trendRes] = await Promise.all([
          api.get(`/dashboard/stats${bq}`),
          api.get("/memberships?page=1&limit=100"),
          api.get(`/memberships/revenue-trend${bq}`),
        ]);

        const memberships = membershipsRes.data.memberships || [];
        const activeCount = memberships.filter(m => m.status === "Active").length;
        const partialCount = memberships.filter(m => m.status === "Partial").length;
        const expiredCount = memberships.filter(m => m.status === "Expired").length;
        const totalRevenue = memberships.reduce((sum, m) => sum + (m.paidAmount || 0), 0);
        const pendingAmount = memberships.reduce((sum, m) => sum + (m.remainingAmount || 0), 0);

        setStats({
          ...dashRes.data,
          totalMemberships: memberships.length,
          activeMemberships: activeCount,
          partialMemberships: partialCount,
          expiredMemberships: expiredCount,
          totalRevenue,
          pendingAmount,
        });

        setRevenueTrend(trendRes.data.trend || []);

        const pkgMap = {};
        for (const m of memberships) {
          const name = m.packageName || "Unknown";
          pkgMap[name] = (pkgMap[name] || 0) + (m.paidAmount || 0);
        }
        const pkgData = Object.entries(pkgMap)
          .map(([name, revenue]) => ({
            name: name.length > 16 ? name.slice(0, 16) + "…" : name,
            revenue,
          }))
          .sort((a, b) => b.revenue - a.revenue);
        setRevenueByPackage(pkgData);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [currentBranch, canViewOverview]);

  // ── Access denied ────────────────────────────────────────────
  if (!canViewOverview) {
    return (
      <div className="space-y-6" data-testid="memberships-overview-page">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Memberships Overview</h1>
          <p className="text-xs tracking-tight text-muted-foreground mt-1">Revenue and membership analytics</p>
        </div>
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-base">Access Restricted</p>
              <p className="text-sm text-muted-foreground mt-1">You don't have permission to view the Memberships Overview.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  const statusData = [
    { name: "Active", value: stats?.activeMemberships || 0, color: "hsl(var(--chart-2))" },
    { name: "Partial", value: stats?.partialMemberships || 0, color: "hsl(var(--chart-3))" },
    { name: "Expired", value: stats?.expiredMemberships || 0, color: "hsl(var(--primary))" },
  ];

  const paymentModeData = Object.entries(stats?.modeBreakdown || {}).map(([name, d]) => ({
    name,
    amount: d.amount,
    count: d.count,
  }));

  const planData = Object.entries(stats?.planCounts || {}).map(([name, count]) => ({
    name: name.length > 16 ? name.slice(0, 16) + "…" : name,
    count,
  }));

  const tooltipStyle = { backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' };
  const axisStyle = { fill: 'hsl(var(--muted-foreground))', fontSize: 10 };

  return (
    <div className="space-y-6" data-testid="memberships-overview-page">
      <div>
        <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Memberships Overview</h1>
        <p className="text-xs tracking-tight text-muted-foreground mt-1">Revenue and membership analytics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
            <p className="text-3xl font-black font-heading mt-3">{stats?.totalMemberships || 0}</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">TOTAL</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <DollarSign className="h-5 w-5 text-emerald-500" />
            <p className="text-3xl font-black font-heading mt-3 text-emerald-500">₹{((stats?.totalRevenue || 0) / 1000).toFixed(0)}K</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">COLLECTED</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <p className="text-3xl font-black font-heading mt-3 text-amber-500">₹{((stats?.pendingAmount || 0) / 1000).toFixed(0)}K</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">PENDING</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-5">
            <TrendingUp className="h-5 w-5 text-primary" />
            <p className="text-3xl font-black font-heading mt-3">₹{((stats?.revenueMonth || 0) / 1000).toFixed(0)}K</p>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest">THIS MONTH</p>
          </CardContent>
        </Card>
      </div>

      {/* Status + Payment Mode */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide">Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[260px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value">
                    {statusData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-2">
              {statusData.map((s) => (
                <div key={s.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="text-xs text-muted-foreground">{s.name}: {s.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide">Revenue by Payment Mode</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={paymentModeData} barSize={36}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 1000}k`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`₹${v.toLocaleString()}`, 'Amount']} />
                  <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Members by Package + Revenue by Package */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide">Members by Package</CardTitle>
          </CardHeader>
          <CardContent>
            {planData.length === 0 ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No data</div>
            ) : (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={planData} layout="vertical" barSize={24}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={axisStyle} axisLine={false} tickLine={false} />
                    <YAxis dataKey="name" type="category" tick={axisStyle} axisLine={false} tickLine={false} width={100} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" fill="hsl(var(--chart-4))" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide">Revenue by Package</CardTitle>
          </CardHeader>
          <CardContent>
            {revenueByPackage.length === 0 ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No data</div>
            ) : (
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueByPackage} layout="vertical" barSize={24}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 1000}k`} />
                    <YAxis dataKey="name" type="category" tick={axisStyle} axisLine={false} tickLine={false} width={100} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`₹${v.toLocaleString()}`, 'Revenue']} />
                    <Bar dataKey="revenue" fill="hsl(var(--chart-2))" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend */}
      <div className="pt-2">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading uppercase tracking-wide flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              Revenue Trend (6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueTrend.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">No payment data available</div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueTrend}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v / 1000}k`} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`₹${v.toLocaleString()}`, 'Revenue']} />
                    <Area type="monotone" dataKey="revenue" stroke="hsl(var(--chart-2))" fillOpacity={1} fill="url(#colorRevenue)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
